import { ApplicationConfig, DEFAULT_CURRENCY_CODE, inject, isDevMode, LOCALE_ID, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { provideNgxUiKitLocale, provideNgxUiKitToast, provideNgxUiKitTranslation } from '@nesea/ngx-ui-kit';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TranslocoMissingHandlerData, TranslocoService } from '@jsverse/transloco';
import { catchError, firstValueFrom, forkJoin, of, timeout } from 'rxjs';
import { WINDOW } from '@core/tokens';
import { StorageService } from '@core/services/storage.service';
import { coreInterceptor } from '@core/interceptors/core.interceptor';
import { loadConfigurationFactory } from '@core/factories/load-configuration.factory';
import { ConfigService } from '@core/services/config.service';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { CoreStore } from '@core/store/core.store';
import { environment } from '@env/environment';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import localeIt from '@angular/common/locales/it';
import { registerLocaleData } from '@angular/common';
import { HashMap } from '@nesea/ngx-ui-kit/shared';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';

registerLocaleData(localeIt, 'it-IT');

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimations(),
    provideAppInitializer(() => {
      const configService: ConfigService = inject(ConfigService);
      const translocoService: TranslocoService = inject(TranslocoService);
      // Eagerly instantiate the CoreStore: `init()` must be chained onto this initializer
      // rather than registered as a separate one, otherwise it would run before config$
      // resolves.
      const coreStore = inject(CoreStore);
      const config$ = loadConfigurationFactory(configService);
      const translation$ = translocoService.load('it').pipe(
        timeout(8000),
        catchError((err) => {
          console.error('Impossibile caricare le traduzioni (it.json) entro il timeout previsto; il bootstrap procede comunque.', err);
          return of(null);
        })
      );

      return firstValueFrom(forkJoin([config$, translation$]))
        .then(() => coreStore.init());
    }),
    provideHttpClient(withXhr(),
      withInterceptors([coreInterceptor]),
    ),
    {
      provide: WINDOW,
      useValue: window
    },
    StorageService,
    {
      provide: LOCALE_ID,
      useValue: 'it-IT'
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'EUR'
    },
    provideNgxUiKitLocale(),
    provideNgxUiKitToast({
      timeOut: 10000,
      disableTimeOut: false,
      placement: 'top-end'
    }),
    provideNgxUiKitTranslation({
      config: {
        availableLangs: ['it', 'en'],
        defaultLang: 'it',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          allowEmpty: true
        }
      },
      locale: {
        langToLocaleMapping: {
          'it': 'it-IT',
          'en': 'en-US'
        },
        localeConfig: {
          global: {
            date: {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            }
          }
        }
      },
      messageFormat: {
        locales: ['it-IT', 'en-US']
      },
      missingHandlerFn: (key: string, data?: TranslocoMissingHandlerData, params?: HashMap) => key
    }),
    // Empty root reducer map: the core runs on @ngrx/signals (CoreStore), but the 21
    // feature stores are still classic NgRx and need the root store registered.
    provideStore({}, {
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictActionSerializability: false,
        strictStateSerializability: false
      }
    }),
    // No root effects: this only bootstraps the runner for the feature stores.
    provideEffects(),
    !environment.production ? provideStoreDevtools({
      name: 'NSA nservices',
      maxAge: 25,
      logOnly: environment.production
    }) : [], provideCharts(withDefaultRegisterables())
  ]
};
